import { PortfolioSchemeData } from "@/type/comparePageTypes";
import {
  FundSIFSuitability,
  MarketConditionItem,
  MarketConditionStrategyContent,
  MarketConditionTabsCategory,
  MarketConditiontypeSeriesKey,
  RiskCategory,
  SIFPortfolioData,
  SIFResultArray,
  SIFStrategyAnswers,
  SIFStrategyApiResponse,
  SIFStrategyGoal,
  SIFStrategyHorizon,
  SIFStrategyIsinMap,
  SIFStrategyQuizOption,
  SIFStrategyQuizQuestion,
  SIFStrategyReaction,
} from "@/type/sifPageTypes";

/*  -----------------
----------------  */
export const riskCategoryDescriptions: Record<RiskCategory, string> = {
  "very low": "Capital preservation with highly conservative allocation and strict downside protection.",
  low: "Stable returns with limited volatility through cautious, well-diversified positioning.",
  medium: "Balanced growth and stability with selective opportunities and controlled risk exposure.",
  "moderate to high": "Growth-oriented approach with active allocation and disciplined risk management.",
  high: "High-conviction bets aimed at strong returns, backed by active monitoring and risk controls.",
  "very high": "Aggressive, high-conviction strategies maximizing upside with active risk control.",
};

/*  -----------------
----------------  */
export function parseFundSIFSuitability(meta: string | null | undefined): FundSIFSuitability | null {
  if (!meta) return null;

  try {
    const parsed: unknown = JSON.parse(meta);

    if (typeof parsed === "object" && parsed !== null) {
      return parsed as FundSIFSuitability;
    }

    return null;
  } catch {
    return null;
  }
}

/*  ------------------
---------------  */
export function transformSIFPortfolioData(data: PortfolioSchemeData | null | undefined): SIFPortfolioData | null {
  if (!data) return null;

  const transformedResultArray: SIFResultArray = Object.entries(data.resultArray).reduce((acc, [key, value]) => {
    acc[key] = {
      sector: value.sector ?? [],
      market_cap: value.market_cap ?? [],
    };
    return acc;
  }, {} as SIFResultArray);

  return {
    tabList: data.tabList ?? [],
    resultArray: transformedResultArray,
  };
}

/*  ------------------
---------------  */
const toValue = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .trim()
    .replace(/\s+/g, "_");

export function transformQuizData(data: SIFStrategyApiResponse): SIFStrategyQuizQuestion[] {
  return data
    .flat() // remove nested arrays
    .sort((a, b) => a.step - b.step) // ensure correct order
    .map(
      (q): SIFStrategyQuizQuestion => ({
        id: q.id,
        title: q.stepper_title,
        step: q.step,
        question: q.question,
        options: q.options.map(
          (opt): SIFStrategyQuizOption => ({
            id: opt.id,
            label: opt.option || opt.description,
            value: toValue(opt.option),
          }),
        ),
      }),
    );
}

/*  ------------------
---------------  */
const isinMap: SIFStrategyIsinMap = {
  "Capital Protection": {
    Hold: {
      "<3Y": "INF200K30015",
      "3-5Y": "INF109K30018",
      "5Y+": "INF194K30010",
    },
    "Invest More": {
      "<3Y": "INF200K30015",
      "3-5Y": "INF109K30018",
      "5Y+": "INF194K30010",
    },
    Sell: {
      "<3Y": "INF200K30015",
      "3-5Y": "INF109K30018",
      "5Y+": "INF109K30018",
    },
  },
  "Steady Growth": {
    Hold: {
      "<3Y": "INF194K30010",
      "3-5Y": "INF579M30018",
      "5Y+": "INF966L30027",
    },
    "Invest More": {
      "<3Y": "INF579M30018",
      "3-5Y": "INF579M30018",
      "5Y+": "INF966L30027",
    },
    Sell: {
      "<3Y": "INF109K30018",
      "3-5Y": "INF194K30010",
      "5Y+": "INF579M30018",
    },
  },
  "Wealth Creation": {
    Hold: {
      "<3Y": "INF579M30018",
      "3-5Y": "INF109K30034",
      "5Y+": "INF109K30034",
    },
    "Invest More": {
      "<3Y": "INF579M30018",
      "3-5Y": "INF966L30183",
      "5Y+": "INF966L30183",
    },
    Sell: {
      "<3Y": "INF579M30018",
      "3-5Y": "INF579M30018",
      "5Y+": "INF579M30018",
    },
  },
};

const goalMap: Record<string, SIFStrategyGoal> = {
  preserve_capital: "Capital Protection",
  moderate_growth: "Steady Growth",
  high_growth: "Wealth Creation",
};

const reactionMap: Record<string, SIFStrategyReaction> = {
  panic_sell: "Sell",
  hold: "Hold",
  buy_dip: "Invest More",
};

const horizonMap: Record<string, SIFStrategyHorizon> = {
  short_term: "<3Y",
  medium_term: "3-5Y",
  long_term: "5Y+",
};

export function getIsin(
  goal: SIFStrategyGoal,
  reaction: SIFStrategyReaction,
  horizon: SIFStrategyHorizon,
): string | null {
  return isinMap[goal]?.[reaction]?.[horizon] ?? null;
}

export function getIsinFromAnswers(answers: SIFStrategyAnswers): string | null {
  const goalValue = answers[1];
  const reactionValue = answers[2];
  const horizonValue = answers[3];

  if (!goalValue || !reactionValue || !horizonValue) return null;

  const goal = goalMap[goalValue];
  const reaction = reactionMap[reactionValue];
  const horizon = horizonMap[horizonValue];

  if (!goal || !reaction || !horizon) return null;

  return getIsin(goal, reaction, horizon);
}

/*  ------------------
    Static data and helper functions for SIF page
---------------  */
export const marketConditioncolumnsDesk = [
  { title: "SIP (Mutual Funds)" },
  { title: "SIF", highlight: true },
  { title: "PMS" },
  { title: "AIF (Cat III)" },
];

export const marketConditioncolumnsMob = [{ title: "SIP/MF" }, { title: "SIF" }, { title: "PMS" }, { title: "AIF" }];

export const marketConditionrows = [
  {
    feature: "Minimum Investment",
    values: ["₹100", "₹10L", "₹50L", "₹1Cr"],
  },
  {
    feature: "Strategy Flexibility",
    values: ["Low (Long Only)", "High (Long/Short)", "Very High", "Maximum"],
  },
  {
    feature: "Downside Protection",
    values: ["None (Passive)", "Active Hedging", "Fund Manager Discretion", "Sophisticated Hedging"],
  },
  {
    feature: "Tax Efficiency",
    values: ["Equity Taxation", "Equity Taxation", "Complex", "Pass Through (High Tax)"],
  },
  {
    feature: "Liquidity",
    values: ["Daily", "Daily/Weekly", "Daily", "Locked-in (3-5 Yrs)"],
  },
  {
    feature: "Personalization",
    values: ["Generic", "Semi-Custom", "High", "Very High"],
  },
  {
    feature: "Ideal Investor",
    values: ["Retail", "Emerging HNI", "HNI", "UHNI / Family Office"],
  },
  {
    feature: "Hidden Costs",
    values: ["None", "Zero", "Setup Fees + Profit Share", "High Setup + Carry"],
  },
  {
    feature: "Regulatory Safety",
    values: ["Strict (SEBI MF)", "Strict (SEBI MF)", "Moderate", "Light Regulation"],
  },
];

export const seriesMeta: {
  key: MarketConditiontypeSeriesKey;
  name: string;
  color: string;
  dashStyle?: Highcharts.DashStyleValue;
}[] = [
  {
    key: "sif",
    name: "SIF Strategy",
    color: "#F59E0B",
  },
  {
    key: "nifty",
    name: "Benchmark",
    color: "#D1D5DB",
    dashStyle: "Dash",
  },
];

export const strategyContent: Record<MarketConditionTabsCategory, Record<string, MarketConditionStrategyContent>> = {
  equity: {
    "Long-Short": {
      title: "Equity Long-Short Fund Strategy",
      description:
        "Invests minimum 80% in equity, with up to 25% in unhedged short positions via derivatives with an aim to manage downside risk.",
      cards: [
        {
          title: "Long Positions",
          description: "Buying quality stocks expected to rise (e.g., HDFC Bank, Infosys).",
        },
        {
          title: "Short Positions (Hedging)",
          description: "Selling index futures (Nifty/Bank Nifty) to offset market falls.",
        },
      ],
    },

    "Ex-Top 100": {
      title: "Equity Ex-Top 100 Long-Short Fund",
      description:
        "Invests at least 65% in stocks outside the top 100 by market cap, targeting mid and small-cap efficiencies to generate alpha.",
      cards: [],
    },

    "Sector Rotation": {
      title: "Sector Rotation Long-Short Fund",
      description: "Maintains 80%+ equity across up to four sectors, rotating based on macro trends.",
      cards: [],
    },
  },

  debt: {
    "Long-Short": {
      title: "Debt Long-Short Strategy Fund",
      description: "Invests in debt instruments of varying maturities and can take short positions via derivatives.",
      cards: [],
    },

    "Sectoral Debt": {
      title: "Sectoral Debt Long-Short Strategy",
      description: "Focuses on debt across at least two sectors with a 75% cap per sector.",
      cards: [],
    },
  },

  hybrid: {
    "Long-Short": {
      title: "Hybrid Long-Short Strategy Fund",
      description: "Allocates minimum 25% each to equity and debt with derivatives balancing risk.",
      cards: [],
    },

    "Active Asset Allocator": {
      title: "Active Asset Allocator Fund",
      description: "Dynamic allocation across equity, debt, REITs, InvITs, and commodities.",
      cards: [],
    },
  },
};

export const staticSeriesArray: MarketConditionItem[] = [
  {
    category: "hybrid",
    type: "bull",
    nifty: [0, 4, 6, 9, 12],
    sif: [0, 6, 9, 12, 16],
  },
  {
    category: "hybrid",
    type: "bear",
    nifty: [0, -3, -5, -7, -9],
    sif: [0, -2, -4, -6, -8],
  },

  {
    category: "debt",
    type: "bull",
    nifty: [0, 5, 8, 11, 15],
    sif: [0, 9, 12, 16, 20],
  },
  {
    category: "debt",
    type: "bear",
    nifty: [0, -4, -7, -9, -12],
    sif: [0, -2, -5, -8, -10],
  },

  {
    category: "equity",
    type: "bull",
    nifty: [0, 3, 5, 7, 10],
    sif: [0, 5, 7, 9, 14],
  },
  {
    category: "equity",
    type: "bear",
    nifty: [0, -2, -4, -6, -8],
    sif: [0, -1.5, -3.5, -5.5, -7],
  },
];
